/**
 * Application Routes Configuration
 * Single source of truth for all route definitions
 */

export const ROUTES = {
  // Public routes (no authentication required)
  PUBLIC: {
    HOME: "/",
    LOGIN: "/login",
    REGISTER: "/register",
    FORGOT_PASSWORD: "/forgot-password",
    FORGOT_PASSWORD_VERIFICATION: "/forgot-password-verification",
    RESET_PASSWORD: "/reset-password",
    RESET_SUCCESS: "/reset-success",
    VERIFICATION_SUCCESS: "/verification-success",
    PRIVACY_POLICY: "/privacy-policy",
    TERMS_CONDITIONS: "/terms-conditions",
    AML_COMPLIANCE: "/aml-compliance",
    USDT: "/usdt",
    BTC: "/btc",
    ETH: "/eth",
    SOL: "/sol",
    USDC: "/usdc"
  },

  // Registration flow routes
  REGISTRATION: {
    VERIFY_EMAIL: "/verify-email",
  },

  // Auth-protected routes (requires token but not KYC)
  AUTH_PROTECTED: {
    COMPLETE_PROFILE: "/complete-profile",
  },

  // KYC-protected routes (requires token + verified KYC)
  KYC_PROTECTED: {
    ANALYTICS: "/analytics",
    BENEFICIARIES: "/beneficiaries",
    DASHBOARD: "/dashboard",
    RECEIVE: "/receive",
    SEND: "/send",
    SETTINGS: "/settings",
    TRANSACTIONS: "/transactions",
  },
} as const;

/**
 * Get array of public route paths
 */
export const getPublicRoutes = () => [...Object.values(ROUTES.PUBLIC)];

/**
 * Get array of registration route paths
 */
export const getRegistrationRoutes = () => [
  ...Object.values(ROUTES.REGISTRATION),
];

/**
 * Get array of KYC-protected route paths
 */
export const getKycProtectedRoutes = () => [
  ...Object.values(ROUTES.KYC_PROTECTED),
];

/**
 * Get array of all auth-protected routes (requires token)
 */
export const getAuthProtectedRoutes = () => [
  ...Object.values(ROUTES.AUTH_PROTECTED),
  ...Object.values(ROUTES.KYC_PROTECTED),
];

/**
 * Check if a path is a public route
 */
// export const isPublicRoute = (path: string): boolean => {
//   return getPublicRoutes().some(route => path.startsWith(route));
// };

export const isPublicRoute = (path: string): boolean => {
  return getPublicRoutes().some((route) =>
    route === "/"
      ? path === "/"
      : path === route || path.startsWith(`${route}/`)
  );
};

/**
 * Check if a path is a registration route
 */
export const isRegistrationRoute = (path: string): boolean => {
  return getRegistrationRoutes().some((route) => path.startsWith(route));
};

/**
 * Check if a path requires KYC verification
 */
export const isKycProtectedRoute = (path: string): boolean => {
  return getKycProtectedRoutes().some((route) => path.startsWith(route));
};

/**
 * Check if a path requires authentication
 */
export const isAuthProtectedRoute = (path: string): boolean => {
  return getAuthProtectedRoutes().some((route) => path.startsWith(route));
};

/**
 * Get the default redirect route based on KYC status
 */
export const getDefaultRedirect = (isKycVerified: boolean): string => {
  return isKycVerified
    ? ROUTES.KYC_PROTECTED.ANALYTICS
    : ROUTES.AUTH_PROTECTED.COMPLETE_PROFILE;
};
